import React, { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useDimensionsRef } from 'hooks';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import { useLaborCostDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store';
import { STACKED_BAR_CATEGORIES } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/LaborCostDashboard.constants';
import { REPORTS_PAGE_COLORS } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/LaborCostDashboard.styled';
import {
  StackedBarContainer,
  LegendRow,
  LegendItem,
  LegendSwatch,
} from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/TypesOfHoursChart.styled';
import { TypesOfHoursEntry } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store.types';

const CHART_HEIGHT = 360;
const MARGIN = { top: 8, right: 20, bottom: 64, left: 50 };
const BAR_PADDING = 0.12;
const TWO_WEEKS_DAY_COUNT = 14;
const Y_AXIS_HEADROOM_FACTOR = 1.05;

const CATEGORY_KEYS = STACKED_BAR_CATEGORIES.map((c) => c.key);
const STACK_KEYS = [...CATEGORY_KEYS].reverse();
const COLOR_BY_KEY: Record<string, string> = Object.fromEntries(
  STACKED_BAR_CATEGORIES.map((c) => [c.key, c.color]),
);
const LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  STACKED_BAR_CATEGORIES.map((c) => [c.key, c.label]),
);

function formatDateLabel(dateStr: string): string {
  const isoPart = dateStr.split(' ')[0];
  const d = new Date(isoPart + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return dateStr;
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  return `${month} ${day}`;
}

interface StackedLayerSegmentProps {
  segKey: string;
  barX: number;
  barWidth: number;
  y0: number;
  y1: number;
  color: string;
  label: string;
  date: string;
  value: number;
  hovered: string | null;
  setHovered: (key: string | null) => void;
  tooltip: ReturnType<typeof useChartTooltip>;
}

const StackedLayerSegment: React.FC<StackedLayerSegmentProps> = ({
  segKey,
  barX,
  barWidth,
  y0,
  y1,
  color,
  label,
  date,
  value,
  hovered,
  setHovered,
  tooltip,
}) => {
  const segHeight = y0 - y1;
  if (segHeight <= 0) return null;
  const valueText = formatDashboardNumber(value);
  return (
    <rect
      key={segKey}
      x={barX}
      y={y1}
      width={barWidth}
      height={segHeight}
      fill={color}
      opacity={hovered === segKey ? 0.8 : 1}
      style={{ cursor: 'pointer' }}
      role="img"
      aria-label={`${date} ${label}: ${valueText}`}
      tabIndex={0}
      onMouseEnter={(e) => {
        setHovered(segKey);
        tooltip.show(e, `<strong>${date}</strong><br/>${label}: ${valueText}h`);
      }}
      onMouseMove={(e) => tooltip.move(e)}
      onMouseLeave={() => {
        setHovered(null);
        tooltip.hide();
      }}
      onFocus={(e) => {
        setHovered(segKey);
        tooltip.show(e, `<strong>${date}</strong><br/>${label}: ${valueText}h`);
      }}
      onBlur={() => {
        setHovered(null);
        tooltip.hide();
      }}
    />
  );
};

interface StackedLayerProps {
  layer: d3.Series<TypesOfHoursEntry, string>;
  layerKey: string;
  xScale: d3.ScaleBand<string>;
  yScale: d3.ScaleLinear<number, number>;
  hovered: string | null;
  setHovered: (key: string | null) => void;
  tooltip: ReturnType<typeof useChartTooltip>;
}

const StackedLayer: React.FC<StackedLayerProps> = ({
  layer,
  layerKey,
  xScale,
  yScale,
  hovered,
  setHovered,
  tooltip,
}) => {
  const color = COLOR_BY_KEY[layerKey];
  const label = LABEL_BY_KEY[layerKey];
  return (
    <>
      {layer.map((segment) => {
        const segKey = `${segment.data.date}-${layerKey}`;
        return (
          <StackedLayerSegment
            key={segKey}
            segKey={segKey}
            barX={xScale(segment.data.date) || 0}
            barWidth={xScale.bandwidth()}
            y0={yScale(segment[0])}
            y1={yScale(segment[1])}
            color={color}
            label={label}
            date={segment.data.date}
            value={segment[1] - segment[0]}
            hovered={hovered}
            setHovered={setHovered}
            tooltip={tooltip}
          />
        );
      })}
    </>
  );
};

export const TypesOfHoursChart: React.FC = () => {
  const data = useLaborCostDashboardStore((s) => s.typesOfHours);
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<string | null>(null);

  const innerWidth = Math.max(containerWidth - MARGIN.left - MARGIN.right, 0);
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const stackedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const stack = d3.stack<TypesOfHoursEntry>().keys(STACK_KEYS);
    return stack(data);
  }, [data]);

  const xScale = useMemo(
    () =>
      d3
        .scaleBand<string>()
        .domain(data?.map((d) => d.date) ?? [])
        .range([0, innerWidth])
        .padding(BAR_PADDING),
    [data, innerWidth],
  );

  const yScale = useMemo(() => {
    if (!data || data.length === 0) return d3.scaleLinear().domain([0, 1]).range([innerHeight, 0]);
    const maxTotal =
      d3.max(data, (d) =>
        CATEGORY_KEYS.reduce((sum, key) => sum + (d[key as keyof TypesOfHoursEntry] as number), 0),
      ) || 1;
    return d3
      .scaleLinear()
      .domain([0, maxTotal * Y_AXIS_HEADROOM_FACTOR])
      .range([innerHeight, 0]);
  }, [data, innerHeight]);

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const showEveryNth = useMemo(() => {
    if (!data) return 1;
    if (data.length <= TWO_WEEKS_DAY_COUNT) return 1;
    return Math.ceil(data.length / 10);
  }, [data]);

  if (!data) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <StackedBarContainer>
      <LegendRow>
        {STACKED_BAR_CATEGORIES.map((cat) => (
          <LegendItem key={cat.key}>
            <LegendSwatch $color={cat.color} />
            <span>{cat.label}</span>
          </LegendItem>
        ))}
      </LegendRow>
      <div ref={containerRef} style={{ width: '100%' }}>
        {containerWidth > 0 && (
          <svg
            width={containerWidth}
            height={CHART_HEIGHT}
            aria-label="Types of Hours stacked bar chart"
          >
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {yTicks.map((tick) => (
                <g key={tick}>
                  <line
                    x1={0}
                    x2={innerWidth}
                    y1={yScale(tick)}
                    y2={yScale(tick)}
                    stroke={REPORTS_PAGE_COLORS.gridLine}
                  />
                  <text
                    x={-8}
                    y={yScale(tick)}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize="12px"
                    fill={REPORTS_PAGE_COLORS.axisLabel}
                  >
                    {tick}
                  </text>
                </g>
              ))}
              {stackedData.map((layer, layerIndex) => (
                <StackedLayer
                  key={STACK_KEYS[layerIndex]}
                  layer={layer}
                  layerKey={STACK_KEYS[layerIndex]}
                  xScale={xScale}
                  yScale={yScale}
                  hovered={hovered}
                  setHovered={setHovered}
                  tooltip={tooltip}
                />
              ))}
              {data.map((d, i) => {
                if (i % showEveryNth !== 0) return null;
                const barX = xScale(d.date) || 0;
                const centerX = barX + xScale.bandwidth() / 2;
                const labelY = innerHeight + 8;
                return (
                  <text
                    key={d.date}
                    x={centerX}
                    y={labelY}
                    textAnchor="end"
                    dominantBaseline="central"
                    fontSize="12px"
                    fill={REPORTS_PAGE_COLORS.axisLabel}
                    transform={`rotate(-90, ${centerX}, ${labelY})`}
                  >
                    {formatDateLabel(d.date)}
                  </text>
                );
              })}
            </g>
          </svg>
        )}
      </div>
    </StackedBarContainer>
  );
};
